import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Button from './ui/Button';
import { EASE_OUT, DURATION_MODAL } from '../styles/motion';

function ErrorFallback({ error, errorInfo, onReset }) {
  const { t } = useLanguage();
  const eb = t.errorBoundary;

  return (
    <div className="dashboard-shell flex min-h-screen items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: DURATION_MODAL, ease: EASE_OUT }}
        className="auth-card w-full max-w-2xl p-8 text-center"
      >
        <div className="mb-6">
          <div className="icon-tile mx-auto mb-4 h-20 w-20 from-error-500 to-error-600">
            <ExclamationTriangleIcon className="h-10 w-10 text-white" />
          </div>
          <h1 className="mb-2 text-3xl font-bold text-secondary-900 ">{eb.title}</h1>
          <p className="mb-6 text-secondary-600 ">{eb.message}</p>
        </div>

        {process.env.NODE_ENV === 'development' && error && (
          <details className="surface-elevated mb-6 max-h-64 overflow-auto p-4 text-left">
            <summary className="mb-2 cursor-pointer font-semibold text-secondary-700 ">{eb.details}</summary>
            <pre className="whitespace-pre-wrap text-xs text-error-600">
              {error.toString()}
              {errorInfo?.componentStack}
            </pre>
          </details>
        )}

        <Button
          type="button"
          variant="primary"
          onClick={onReset}
          icon={ArrowPathIcon}
        >
          {eb.reload}
        </Button>
      </motion.div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
